import { Injectable } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { DateUtils, Moment, PrecoModificationResultatsDTO, PreconisationInformationDTO } from "@mra/hlp9002";
import { ChoixPerception, PreconisationModifiee } from "src/app/models/preconisationModifiee.model";
import { MIN_RPP_DUREE } from "src/app/shared/validators/rpp.validator";

export type TypeFormRepartition = {
    rente: FormControl<number>;
    rpp: FormControl<number>;
    capitalPonctuel: FormControl<number>;
};

export type TypeFormRevenuRegulier = {
    montant: FormControl<number>;
    duree: FormControl<number>;
};

export type TypeFormSimConso = {
    dateEffet: FormControl<Moment>;
    repartition: FormGroup<TypeFormRepartition>;
    renteRetenuOption: FormControl<ChoixPerception>;
    revenuRegulier: FormGroup<TypeFormRevenuRegulier>;
};

export type Repartition = FormGroup<TypeFormSimConso>["value"]["repartition"];
export type ChoixSimuConso = FormGroup<TypeFormSimConso>["value"];

@Injectable({
    providedIn: "root"
})
export class PrecoMapperService {
    /**
     * convertit une PreconisationModifiee en ChoixSimuConso
     * @param preco Preconisation modifiée
     * @returns ChoixSimuConso
     */
    precoToChoixSimu(preco: PreconisationModifiee): ChoixSimuConso {
        return {
            dateEffet: DateUtils._(preco.dateEffet),
            repartition: {
                rente: preco.consommationRente ?? 0,
                rpp: preco.consommationRpp ?? 0,
                capitalPonctuel: preco.consommationCapital ?? 0
            },
            renteRetenuOption: preco.choixPerception ?? ChoixPerception.Annuel,
            revenuRegulier: {
                montant: preco.montantRppAnnuel ?? 0,
                duree: preco.dureeRppAnnuel ?? MIN_RPP_DUREE
            }
        };
    }

    /**
     * Convertit les choix de l'utilisateur en PreconisationModifiee
     * @params choix - choix de l'utilisateur dans le formulaire simu conso
     * @params precoInitiale - préconisation initiale
     * @returns PreconisationModifiee
     */
    choixSimuToPreco(choix: ChoixSimuConso, precoInitiale: PreconisationInformationDTO): PreconisationModifiee {
        return {
            ...precoInitiale,
            dateEffet: choix.dateEffet?.format("YYYY-MM-DD"),
            consommationRente: choix.repartition.rente,
            consommationRpp: choix.repartition.rpp,
            consommationCapital: choix.repartition.capitalPonctuel,
            choixPerception: choix.renteRetenuOption,
            montantRppAnnuel: choix.revenuRegulier.montant,
            dureeRppAnnuel: choix.revenuRegulier.duree
        };
    }

    /**
     * Convertit le résultat de l'API de modification de préconisation en ChoixSimuConso
     * @param resultat - résultat de l'API PrecoModificationResultatsDTO
     * @returns ChoixSimuConso partiel (sans dateEffet ni renteRetenuOption)
     */
    precoModificationResultatsToChoixSimu(resultat: PrecoModificationResultatsDTO): Partial<ChoixSimuConso> {
        return {
            repartition: {
                rente: resultat.consommationRente,
                rpp: resultat.rppAnnuel?.consommationRpp,
                capitalPonctuel: resultat.consommationCapital
            },
            revenuRegulier: {
                montant: resultat.rppAnnuel?.montantRppAnnuel ?? 0,
                duree: resultat.rppAnnuel?.dureeRppAnnuel ?? MIN_RPP_DUREE
            }
        };
    }
}
